/**
 * This file is for util functions meant to be shared between LLA and SimpleSites.
 * This split is necessary to keep LLA only deps from getting bundled with SimpleSites.
 */

import { Set } from 'immutable';
import qs, { stringify as qsStringify } from 'qs';
import { v4 as uuidv4 } from 'uuid';
import { useMemo, useDebugValue } from 'react';
import { from, of } from 'rxjs';

import trace from './trace';

export function formatContractId(rawId) {
  const typeAndCountry = rawId.substr(0, 4);
  const time = rawId.substr(4, 4);
  const id = rawId.substr(8);

  const sections = id.length / 3;
  const idSections = [];
  for (let i = 0; i < sections; i += 1) {
    idSections.push(id.substr(i * 3, 3));
  }

  return `${typeAndCountry} ${time} ${idSections.join(' ')}`;
}

export function genAsyncSingleton(resolver) {
  let singleton = null;
  let initialRequest = null;

  const generator = async () => {
    if (singleton) {
      return singleton;
    }

    if (initialRequest) {
      return initialRequest;
    }

    if (typeof resolver === 'function') {
      initialRequest = resolver();
    } else {
      initialRequest = resolver;
    }

    initialRequest
      .then(it => { singleton = it })
      .catch(e => trace.warn('genAsyncSingleton initialRequest failed', e));

    return initialRequest;
  };

  generator.getCurrentValue = () => singleton;

  generator.hasValue = () => (singleton !== null);

  generator.asObservable = () => {
    if (singleton) {
      return of(singleton);
    }

    return from(generator());
  };

  return generator;
}

export function getCurrencyFormatter(unit, options = {}) {
  return new Intl.NumberFormat(global.navigator.language, {
    style: 'currency',
    currency: unit,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options
  });
}

const nonDecimalCurrencies = new Set([
  'MGA', 'BIF', 'CLP', 'PYG', 'DJF', 'RWF', 'GNF', 'UGX',
  'JPY', 'VND', 'VUV', 'XAF', 'KMF', 'KRW', 'XOF', 'XPF'
]);

function centsToFullUnits(amount, isDecimal) {
  if (isDecimal) {
    return amount / 100;
  }

  return amount;
}

export function getCurrencyFormatterForCents(unit, options = {}) {
  const { showDecimals, ...numberFormatOptions } = options;
  const isDecimal = !nonDecimalCurrencies.has(unit.toUpperCase());

  const fmt = new Intl.NumberFormat(global.navigator.language, {
    style: 'currency',
    currency: unit,
    minimumFractionDigits: (showDecimals && isDecimal) ? 2 : 0,
    maximumFractionDigits: isDecimal ? 2 : 0,
    ...numberFormatOptions
  });

  return amount => fmt.format(centsToFullUnits(amount, isDecimal));
}

/*
 We need a common def for no-operation (noop) in order to get equality checks
 right: prop === noop
 */
export const noop = () => {};

export function parseSearch(search) {
  return qs.parse(search.slice(1));
}

export function pick(what, fromObj) {
  const res = {};
  what.forEach(key => {
    if (typeof fromObj[key] !== 'undefined') {
      res[key] = fromObj[key];
    }
  });

  return res;
}

function strLocaleSort(a, b) { return a.localeCompare(b) }

export function stringifySearch(search, opts = {}) {
  return qsStringify(search, {
    addQueryPrefix: true,
    encode: false, // TODO: Check and test all code to see if this could be true by default.
    sort: strLocaleSort,
    ...opts
  });
}

// Can be used to generate unique ID values for binding labels to their respective labelees.
export function useId(deps = []) { // eslint-disable-next-line react-hooks/exhaustive-deps
  const id = useMemo(uuidv4, deps);
  useDebugValue(id);
  return id;
}

export function getTransitionNames(s, root) {
  return {
    enter: s[`${root}-enter`],
    enterActive: s[`${root}-enter-active`],
    enterDone: s[`${root}-enter-done`],
    exit: s[`${root}-exit`],
    exitActive: s[`${root}-exit-active`],
    exitDone: s[`${root}-exit-done`],
    appear: s[`${root}-appear`],
    appearActive: s[`${root}-appear-active`],
    appearDone: s[`${root}-appear-done`]
  };
}

export function endWhenTransitionEnds(node, done) {
  node.addEventListener('transitionend', e => {
    e.stopPropagation();
    done();
  }, false);
}

// email address heuristic check
const EMAIL_REGEXP = /[^,. ]+(?:\.[^,. ]+)*@[^, ]+\.[^, ]+/;
export function validEmail(email) {
  return EMAIL_REGEXP.test(email);
}

/**
 * @template T
 * @param {function(): Promise<T>} tryFn Promise action to retry. Example: () => import('./A')
 * @param {number=} maxTries Maximum number of tries to do.
 * @param {(function(number, number): number)=} delayFn How many milliseconds to wait between tries.
 *                                                      Receives next try's number and maxTries.
 * @returns {Promise<T>}
 */
export function retry(tryFn, {
  maxTries = 5,
  delayFn = () => 1000
} = {}) {
  return new Promise((resolve, reject) => {
    let tryCount = 0;

    const perform = () => tryFn()
      .then(resolve)
      .catch(error => {
        tryCount += 1;
        if (tryCount === maxTries) {
          reject(error);
        } else {
          setTimeout(perform, delayFn(tryCount, maxTries));
        }
      });

    perform();
  });
}
